import { useState, useEffect } from 'react';
import {
  Loader2, Rss, FileText, Globe, Clock, Image as ImageIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';


const WINDOWS = [
  { id: 'day',   label: 'Today' },
  { id: 'week',  label: 'Last 7 days' },
  { id: 'month', label: 'Last 30 days' },
];

function fmtMs(ms) {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}


function StatCard({ icon: Icon, label, value, sub, tone }) {
  const toneClass = tone === 'good' ? 'text-emerald-600' : 'text-slate-900';
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">
        <Icon size={12} /> {label}
      </div>
      <p className={`text-2xl font-bold ${toneClass}`}>{value}</p>
      {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}


export default function PublicationMetricsTab({ pubId }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [window, setWindow]   = useState('week');

  useEffect(() => { load(); }, [pubId, window]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { window };
      if (pubId) params.publication_id = pubId;
      const { data } = await api.get('/feeds/metrics', { params });
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };

  const funnel   = data?.funnel   || {};
  const timing   = data?.timing   || {};
  const images   = data?.images   || {};
  const perSite  = data?.per_site || [];

  const fetched   = funnel.fetched   || 0;
  const generated = funnel.generated || 0;
  const published = funnel.published || 0;
  const genRate   = fetched   > 0 ? Math.round((generated / fetched) * 100)   : 0;
  const pubRate   = generated > 0 ? Math.round((published / generated) * 100) : 0;

  const imgTotal = (images.source || 0) + (images.library || 0) + (images.ai || 0) + (images.none || 0);
  const pct = (n) => imgTotal > 0 ? Math.round((n / imgTotal) * 100) : 0;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <p className="text-sm text-slate-500">
          EventFlow throughput {pubId ? 'for this publication' : 'across the network'}.
        </p>
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg border border-slate-200">
          {WINDOWS.map(w => (
            <button
              key={w.id}
              onClick={() => setWindow(w.id)}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                window === w.id
                  ? 'bg-white text-blue-600 border border-slate-200 shadow-sm'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >{w.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 gap-2 text-slate-400 text-sm">
          <Loader2 size={16} className="animate-spin" /> Loading metrics...
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <StatCard icon={Rss}      label="Fetched"   value={fetched} sub="items ingested" />
            <StatCard icon={FileText} label="Generated" value={generated} sub={`${genRate}% of fetched`} />
            <StatCard icon={Globe}    label="Published" value={published} sub={`${pubRate}% of generated`} tone="good" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-3">
                <Clock size={12} /> AI rewrite time
              </div>
              <div className="flex items-end gap-6">
                <div>
                  <p className="text-2xl font-bold text-slate-900">{fmtMs(timing.avg_ai_ms)}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">average</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-slate-500">{fmtMs(timing.max_ai_ms)}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">slowest</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-slate-500">{timing.stories || 0}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">stories timed</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-3">
                <ImageIcon size={12} /> Image source
              </div>
              <div className="space-y-2">
                <ImageBar label="Fetched" value={images.source || 0}  pct={pct(images.source || 0)}  colour="bg-emerald-500" />
                <ImageBar label="Library" value={images.library || 0} pct={pct(images.library || 0)} colour="bg-blue-500" />
                <ImageBar label="AI"      value={images.ai || 0}      pct={pct(images.ai || 0)}      colour="bg-violet-500" />
                <ImageBar label="None"    value={images.none || 0}    pct={pct(images.none || 0)}    colour="bg-slate-300" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900">Per-site breakdown</h3>
            </div>
            {perSite.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-slate-400">No activity in this window.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                    <th className="text-left font-semibold px-4 py-2">Publication</th>
                    <th className="text-right font-semibold px-4 py-2">Generated</th>
                    <th className="text-right font-semibold px-4 py-2">Published</th>
                  </tr>
                </thead>
                <tbody>
                  {perSite.map(row => (
                    <tr key={row.publication_id} className="border-b border-slate-50 last:border-b-0 hover:bg-slate-50/50">
                      <td className="px-4 py-2.5 text-slate-900">{row.publication_name}</td>
                      <td className="px-4 py-2.5 text-right text-slate-600">{row.generated}</td>
                      <td className="px-4 py-2.5 text-right text-emerald-600 font-medium">{row.published}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}


function ImageBar({ label, value, pct, colour }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className="text-slate-500">{label}</span>
        <span className="text-slate-400">{value} · {pct}%</span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${colour} rounded-full`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}